import { motion } from "framer-motion";
import { Download, ArrowLeft, MessageCircle } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { WHATSAPP_URL, PHONE_DISPLAY } from "../data/content";

const MENUS = [
  { title: "Muffins — Retail Boxes", desc: "Box of 4 from R120 · Box of 6 from R180", file: "/assets/menus/muffins.png", type: "image" },
  { title: "Muffins — Bulk Orders", desc: "5L, 10L and 20L batches for events & offices", file: "/assets/menus/muffins-bulk.png", type: "image" },
  { title: "Scones", desc: "Classic, Raisin, Savory & Blueberry — 2.5L to 20L", file: "/assets/menus/scones.png", type: "image" },
  { title: "Juices & Pantry", desc: "Fresh juices, immune shots, kombucha, pickles & sauces", file: "/assets/menus/juices-pantry.png", type: "image" },
  { title: "Novaphi Brunch Box", desc: "R180 — wrap, side, muffin & juice, your choice of each", file: "/assets/products/brunch-box.jpg", type: "image" },
  { title: "Weekly Meal Prep", desc: "Individual & family plans — 3 or 5 meals per week", file: "/assets/menus/weekly-meal-prep.pdf", type: "pdf" },
];

export default function Menu() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F9F9F6]" data-testid="menu-page">
      <header className="border-b border-[#F9F9F6]/10 bg-[#0A0A0A]/95 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-[70px] flex items-center justify-between">
          <a href="/" data-testid="menu-back-home" className="flex items-center gap-3 text-[#F9F9F6]/70 hover:text-[#D4AF37] transition-colors text-xs uppercase tracking-[0.2em]">
            <ArrowLeft size={15} strokeWidth={1.5} /> Back to site
          </a>
          <img src="/assets/logo-white.png" alt="Novaphi Catering" className="h-12 w-auto" />
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="menu-whatsapp-btn"
             className="flex items-center gap-2 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] px-4 py-2 text-[10px] uppercase tracking-[0.15em] hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors">
            <MessageCircle size={13} strokeWidth={1.5} /> <span className="hidden sm:inline">{PHONE_DISPLAY}</span><span className="sm:hidden">WhatsApp</span>
          </a>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">The Novaphi Menus</p>
          <h1 className="font-serif-e text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight max-w-3xl">
            Browse, download, order.
          </h1>
          <p className="text-[#F9F9F6]/60 text-base md:text-lg leading-relaxed mt-5 max-w-2xl">
            Every menu, beautifully designed and ready to download. Weekly meal prep orders close on Sundays — an 80% deposit secures your slot.
          </p>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {MENUS.map((m, i) => (
            <motion.article key={m.title} data-testid={`menu-card-${m.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                            transition={{ duration: 0.7, delay: (i % 3) * 0.1 }}
                            className="group border border-[#F9F9F6]/10 hover:border-[#D4AF37]/50 transition-colors bg-[#1A1A1A] flex flex-col">
              <a href={m.file} target="_blank" rel="noopener noreferrer" className="block relative aspect-[3/4] overflow-hidden bg-[#F9F9F6]" data-testid={`menu-view-${i}`}>
                {m.type === "image" ? (
                  <img src={m.file} alt={`${m.title} menu`} loading="lazy"
                       className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-700" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-[#F9F9F6] text-[#0A0A0A] gap-3">
                    <img src="/assets/logo-black.png" alt="Novaphi" className="h-14 w-auto" />
                    <p className="font-serif-e text-2xl">Weekly Meal Prep Menu</p>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#0A0A0A]/50">PDF — tap to open</p>
                  </div>
                )}
              </a>
              <div className="p-5 flex items-center justify-between gap-4 mt-auto">
                <div>
                  <h2 className="font-serif-e text-xl text-[#F9F9F6] leading-snug">{m.title}</h2>
                  <p className="text-[#F9F9F6]/50 text-xs mt-1">{m.desc}</p>
                </div>
                <a href={m.file} download data-testid={`menu-download-${i}`} aria-label={`Download ${m.title}`}
                   className="shrink-0 w-10 h-10 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] flex items-center justify-center hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors">
                  <Download size={15} strokeWidth={1.5} />
                </a>
              </div>
            </motion.article>
          ))}
        </div>

        <Reveal delay={0.1} className="mt-16 text-center">
          <p className="text-[#F9F9F6]/50 text-sm">Need a custom menu for your event?</p>
          <a href="/#contact" data-testid="menu-quote-link"
             className="inline-block mt-4 rounded-full bg-[#D4AF37] text-[#0A0A0A] px-8 py-4 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#F9F9F6] transition-colors">
            Request a Catering Quote
          </a>
        </Reveal>
      </main>

      <footer className="border-t border-[#F9F9F6]/10 py-8 text-center">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#F9F9F6]/30">Novaphi Catering &amp; Events — Where every detail matters.</p>
      </footer>
    </div>
  );
}
